/**
 * PupRing Custom JavaScript - Bundle Builder Individual Module
 * ============================================================
 * Buy 3 for $99 Bundle Builder - Per-Product Customization (Individual Mode)
 *
 * Features:
 * - Per-product customization (individual-1, individual-2, individual-3)
 * - Shows ALL variants (not just common ones)
 * - Per-product pet name inputs based on pendantCount
 * - Per-product photo upload (when required)
 * - Progress dots showing product completion
 *
 * Key Differences from Together mode:
 * - Shows ALL variants (not just common ones)
 * - Each product has its own customization state
 * - Pet name inputs based on specific product's pendantCount
 * - Navigates through products sequentially
 *
 * Dependencies:
 * - custom-bundle-state.js (BundleStateManager)
 * - custom-bundle-ui.js (BundleUIManager)
 * - custom-bundle-photo.js (BundlePhotoUploader)
 */

/**
 * BundleIndividualManager - Handles per-product customization (Individual mode)
 */
class BundleIndividualManager {
  // ═══════════════════════════════════════════════════════════════
  // CONSTRUCTOR
  // ═══════════════════════════════════════════════════════════════

  constructor(stateManager, uiManager, productsData) {
    this.stateManager = stateManager;
    this.uiManager = uiManager;
    this.productsData = productsData;
    this.currentProductIndex = null;
    this.container = null;

    // Photo uploader instance
    this.photoUploader = null;

    // Bound handlers for cleanup
    this.boundVariantHandler = null;
    this.boundPetNameHandler = null;
    this.boundContinueHandler = null;
    this.boundBackHandler = null;

    // BUG-046 FIX: Track async operation validity
    this.asyncToken = 0;
    this.pendingTimers = [];

    // BUG-087 FIX: Track Splide carousel instances
    this.individualSplide = null;
    this.individualThumbsSplide = null;
  }

  // ═══════════════════════════════════════════════════════════════
  // HELPERS
  // ═══════════════════════════════════════════════════════════════

  /**
   * Get current product index from state step
   */
  getProductIndex() {
    const step = this.stateManager.get().currentStep;
    return { 'individual-1': 0, 'individual-2': 1, 'individual-3': 2 }[step] ?? -1;
  }

  /**
   * Get current product from state
   */
  getCurrentProduct() {
    const index = this.getProductIndex();
    if (index < 0) return null;
    return this.stateManager.get().products[index] || null;
  }

  /**
   * Get current product's JSON data
   */
  getProductData() {
    const index = this.getProductIndex();
    if (index < 0 || !this.productsData) return null;
    return this.productsData[index] || null;
  }

  /**
   * Resize Shopify image URL
   */
  resizeImage(url, size) {
    if (!url) return '';
    return this.uiManager?.resizeImage?.(url, size) || url;
  }

  /**
   * Escape HTML for safe rendering
   */
  escapeHtml(str) {
    if (str === null || str === undefined) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // ═══════════════════════════════════════════════════════════════
  // INITIALIZATION
  // ═══════════════════════════════════════════════════════════════

  /**
   * Initialize for specific product
   * @param {number} productIndex - Product index (0, 1, or 2)
   */
  initForProduct(productIndex) {
    if (productIndex < 0 || productIndex > 2) return;

    // Cleanup previous event handlers if switching products
    if (this.currentProductIndex !== null && this.currentProductIndex !== productIndex) {
      this.cleanup();
    }

    // BUG-046 FIX: Invalidate all pending async operations
    this.asyncToken++;

    this.currentProductIndex = productIndex;
    this.container = document.getElementById('bundle-individual-step');

    // BUG-024 FIX: Add error handling instead of silent failure
    if (!this.container) {
      console.error('[BundleIndividualManager] Container #bundle-individual-step not found');
      // Try to show error in sidebar as fallback
      const sidebar = document.getElementById('bundle-builder-sidebar');
      if (sidebar) {
        sidebar.innerHTML = `
          <div class="alert alert-danger">
            <p class="mb-2"><strong>Unable to load customization form</strong></p>
            <p class="mb-0">The page may not have loaded correctly. Please refresh and try again.</p>
          </div>
          <button class="btn btn-outline-secondary mt-3" onclick="window.BundleStateManager?.getInstance()?.goToStep('selection')">
            ← Go Back to Selection
          </button>
        `;
      }
      return;
    }

    // Content now renders directly in container without card wrapper

    // Ensure pendantCount and pendantSize are set from variant
    const state = this.stateManager.get();
    const product = state.products[productIndex];
    const productData = this.productsData?.[productIndex];
    if (product && productData) {
      const variant = productData.variants?.find(v => v.id === product.selectedVariantId);
      if (variant) {
        const pendantCount = this.extractPendantCount(variant, productData);
        const pendantSize = this.extractPendantSize(variant, productData);
        if (pendantCount !== product.pendantCount || pendantSize !== product.pendantSize) {
          // BUG-045 FIX: Update by INDEX, not productId (handles duplicate products)
          this.stateManager.updateProductByIndex(productIndex, { pendantCount, pendantSize });
        }
      }
    }

    this.render();
    this.bindEvents();

    // Initialize photo uploader after render
    this.initPhotoUploader();

    // BUG-087 FIX: Initialize image gallery carousel
    this.initGalleryCarousel();

    // Sync progress bar thumbnails (get fresh state since updateProduct may have modified it)
    this.uiManager?.renderThumbnails?.(this.stateManager.get().products);
  }

  // ═══════════════════════════════════════════════════════════════
  // RENDERING
  // ═══════════════════════════════════════════════════════════════

  /**
   * BUG-099: Calculate dynamic step numbers based on product requirements
   * Step numbers adjust based on what the product needs (material, photo, pet name, engraving)
   * @param {Object} product - Current product with requirements
   * @returns {Object} Step number mapping { material, photo, petName, engraving }
   */
  getStepNumbers(product) {
    const steps = {};
    let stepNum = 1;

    // Material/variant is always Step 1 (if product has variants)
    const productData = this.getProductData();
    const hasVariants = productData?.variants?.length > 1 ||
      (productData?.variants?.length === 1 && productData.variants[0].title !== 'Default Title');

    if (hasVariants) {
      steps.material = stepNum++;
    }

    // Photo upload (if required)
    if (product.requirements?.requiresPhoto) {
      steps.photo = stepNum++;
    }

    // Pet name (if required)
    if (product.requirements?.requiresPetName) {
      steps.petName = stepNum++;
    }

    // Engraving (if available - check for tag)
    const productTags = productData?.tags || [];
    const tagsArray = Array.isArray(productTags)
      ? productTags
      : (typeof productTags === 'string' ? productTags.split(',').map(t => t.trim()) : []);
    const hasEngravingTag = tagsArray.some(tag => tag.toLowerCase() === 'engraving-on-back');

    if (hasEngravingTag) {
      steps.engraving = stepNum++;
    }

    return steps;
  }

  /**
   * BUG-099: Render Photo Guide modal (rendered once, reused by button)
   * @returns {string} HTML string for modal
   */
  renderPhotoGuideModal() {
    // Universal photo guide image from snippets/product-block-photo-guide.liquid
    const photoGuideUrl = 'https://cdn.shopify.com/s/files/1/0369/9854/5539/files/PHOTO_GUIDE_FINAL.jpg?v=1730942607';

    return `
      <div class="modal fade" id="bundle-photo-guide-modal" tabindex="-1" aria-labelledby="bundle-photo-guide-modal-label" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered" style="max-width: 450px;">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title" id="bundle-photo-guide-modal-label">PHOTO GUIDE</h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div class="modal-body">
              <img src="${photoGuideUrl}"
                   alt="Photo guide - tips for best results"
                   class="img-fluid rounded"
                   loading="lazy">
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-outline-dark w-100" data-bs-dismiss="modal">
                Got it!
              </button>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Main render method
   */
  render() {
    // BUG-112: Ensure header is hidden on Individual steps
    // updateStepVisibility() should do this, but class is missing - add it explicitly
    document.body.classList.add('bundle-page-active');

    // BUG-113: Direct style backup - hide header elements directly
    // Targets actual elements, not section group wrapper which may not exist
    document.querySelectorAll('.announcement-bar, #navbar-mobile, #navbar-desktop').forEach(el => {
      el.style.display = 'none';
    });

    const product = this.getCurrentProduct();
    const productData = this.getProductData();
    const index = this.currentProductIndex;

    if (!product || !productData) {
      this.container.innerHTML = `
        <div class="alert alert-danger">
          <p class="mb-0">Unable to load product data. Please go back and try again.</p>
        </div>
        <button class="btn btn-outline-secondary mt-3" data-bundle-individual-back>
          ← Go Back
        </button>
      `;
      return;
    }

    // BUG-099: Calculate step numbers dynamically based on product requirements
    const stepNumbers = this.getStepNumbers(product);

    this.container.innerHTML = `
      <div class="bundle-individual-content">
        <!-- Product Gallery - BUG-087 FIX -->
        ${this.renderGallery(product)}

        <!-- Product Title (moved after gallery) -->
        ${this.renderHeader(product, index)}

        <!-- Variant Selectors (ALL variants) - Materials first -->
        ${this.renderVariantSelectors(product, productData, stepNumbers)}

        <!-- BUG-099 FIX: Photo Upload BEFORE Pet Name -->
        ${this.renderPhotoSection(product, stepNumbers)}

        <!-- Pet Name Inputs - BUG-099: Now after photo -->
        ${this.renderPetNameInputs(product, stepNumbers)}

        <!-- BUG-108 FIX: Product Description moved AFTER pet name (was between Material and Photo) -->
        ${this.renderDescription(productData, index)}

        <!-- Engraving Section (optional, inline) -->
        ${this.renderEngravingSection(product, stepNumbers)}

        <!-- Navigation -->
        ${this.renderNavigation(index)}
      </div>

      <!-- BUG-099: Photo Guide Modal (rendered once, outside main content) -->
      ${this.renderPhotoGuideModal()}
    `;

    // BUG-113: Counter now handled by updateMobileBarValidation() in custom-bundle-ui.js
  }

  /**
   * Render product header (moved under gallery with border)
   */
  renderHeader(product, index) {
    return `
      <div class="bundle-individual-header mb-4 border-bottom" style="padding-bottom: 20px;">
        <h4 class="mb-0">${this.escapeHtml(product.productTitle)}</h4>
      </div>
    `;
  }

  /**
   * Render image gallery with Splide carousel
   * BUG-087 FIX: Full gallery instead of single thumbnail
   * @param {Object} product - Current product state
   * @returns {string} HTML string
   */
  renderGallery(product) {
    const productData = this.getProductData();

    // Fallback if no images
    if (!productData?.images?.length) {
      const image = product.selectedVariantImage || product.featuredImage;
      return `
        <div class="bundle-individual-thumbnail text-center mb-4">
          <img src="${this.resizeImage(image, '300x300')}"
               alt="${this.escapeHtml(product.productTitle)}"
               class="rounded shadow-sm"
               id="bundle-individual-preview-image"
               style="max-width: 300px;">
        </div>
      `;
    }

    const images = productData.images;
    const uniqueId = `bundle-individual-gallery-${this.currentProductIndex}`;
    const thumbsId = `${uniqueId}-thumbs`;

    let html = `
      <div class="bundle-individual-gallery mb-4">
        <div id="${uniqueId}" class="splide">
          <div class="splide__track">
            <ul class="splide__list">
              ${images.map((img, i) => `
                <li class="splide__slide">
                  <img src="${this.resizeImage(img, '400x400')}"
                       alt="${this.escapeHtml(product.productTitle)}"
                       class="img-fluid rounded"
                       loading="${i === 0 ? 'eager' : 'lazy'}">
                </li>
              `).join('')}
            </ul>
          </div>
        </div>
    `;

    if (images.length > 1) {
      html += `
        <div id="${thumbsId}" class="splide mt-2">
          <div class="splide__track">
            <ul class="splide__list">
              ${images.map(img => `
                <li class="splide__slide">
                  <img src="${this.resizeImage(img, '80x80')}"
                       alt="" class="img-fluid rounded" loading="lazy">
                </li>
              `).join('')}
            </ul>
          </div>
        </div>
      `;
    }

    return html + `</div>`;
  }

  /**
   * Render progress dots showing product completion
   */
  renderProgressDots(currentIndex) {
    const state = this.stateManager.get();

    const dots = [0, 1, 2].map(i => {
      const product = state.products[i];
      const isComplete = product ? BundleStateManager.isProductComplete(product) : false;
      const isCurrent = i === currentIndex;
      const isPast = i < currentIndex;

      let className = 'bundle-product-dot';
      if (isCurrent) className += ' active';
      else if (isComplete) className += ' completed';  // BUG-076 FIX: Only show completed for actually complete products, not just "past" ones

      const thumb = product?.selectedVariantImage || product?.featuredImage;

      return `
        <div class="${className}" title="Product ${i + 1}">
          ${thumb
            ? `<img src="${this.resizeImage(thumb, '40x40')}" alt="Product ${i + 1}" width="40" height="40" loading="eager">`
            : `<span>${i + 1}</span>`
          }
          ${isComplete && !isCurrent ? '<span class="dot-check">✓</span>' : ''}
        </div>
      `;
    }).join('');

    return `
      <div class="bundle-individual-progress d-flex justify-content-center gap-3 mb-4">
        ${dots}
      </div>
    `;
  }

  /**
   * Render ALL variant selectors for this product
   * BUG-099: Added step numbers
   * @param {Object} product - Current product
   * @param {Object} productData - Product data from API
   * @param {Object} stepNumbers - Step number mapping
   */
  renderVariantSelectors(product, productData, stepNumbers = {}) {
    // Check if product has only one variant (Default Title)
    if (productData.variants.length === 1 && productData.variants[0].title === 'Default Title') {
      return '';
    }

    // Get the first material/color option
    const materialOption = productData.options.find((opt, idx) => {
      const optName = typeof opt === 'string' ? opt : opt.name;
      return BundleUIManager.isColorOption(optName) ||
             optName.toLowerCase().includes('material') ||
             optName.toLowerCase().includes('color');
    }) || productData.options[0];

    const materialOptionName = typeof materialOption === 'string' ? materialOption : materialOption?.name;

    // Wrap all variants in a styled section
    let html = `<div class="bundle-variant-section">`;

    // Add section title
    html += `<h5 class="bundle-variant-title">Select the material</h5>`;

    // Add variants container
    html += `<div class="bundle-variant-content">`;

    // BUG-018 FIX: Handle both string and object formats for product.options
    html += productData.options.map((option, optIndex) => {
      // BUG-099: Only show step number on first variant option (usually Material)
      const stepNum = optIndex === 0 ? (stepNumbers.material || '') : '';
      const stepLabel = stepNum ? `<span class="bundle-step-number">Step ${stepNum}:</span> ` : '';
      const optionName = typeof option === 'string' ? option : option.name;
      const safeOptionName = this.escapeHtml(optionName);
      const optionKey = `option${optIndex + 1}`;
      const optionHandle = optionName.toLowerCase().replace(/[^a-z0-9]+/g, '-');

      // Get unique values for this option
      const uniqueValues = [...new Set(productData.variants.map(v => v[optionKey]))];

      // Get currently selected value
      const selectedValue = product.selectedOptions?.[optionName] ||
        productData.variants.find(v => v.id === product.selectedVariantId)?.[optionKey] ||
        uniqueValues[0];

      // BUG-034 FIX: Check if this option should be rendered as a color swatch
      const isColorOpt = BundleUIManager.isColorOption(optionName);

      if (isColorOpt) {
        // BUG-032 FIX: Render as card-style swatch matching product page
        // BUG-099: Added step numbers
        return `
          <div class="product-option-wrapper bundle-variant-selector mb-4" data-handle="${optionHandle}">
            <ul class="variant-card-list variant-card-list--swatch list-unstyled mb-0"
                data-option-name="${safeOptionName}" data-option-index="${optIndex}">
              ${uniqueValues.map(value => {
                const isSelected = value === selectedValue;
                const isAvailable = productData.variants.some(v =>
                  v[optionKey] === value && v.available
                );
                const imgUrl = BundleUIManager.getSwatchImageUrl(productData, optIndex, value);
                const valueHandle = value.toLowerCase().replace(/[^a-z0-9]+/g, '-');
                const safeValue = this.escapeHtml(value);

                return `
                  <li class="variant-card-item">
                    <input type="radio"
                           name="bundle-individual-option-${optIndex}"
                           id="bundle-individual-${optIndex}-${valueHandle}"
                           class="bundle-individual-variant-input visually-hidden"
                           data-option-name="${safeOptionName}"
                           data-option-value="${safeValue}"
                           ${isSelected ? 'checked' : ''}
                           ${!isAvailable ? 'disabled' : ''}>
                    <label for="bundle-individual-${optIndex}-${valueHandle}"
                           class="variant-card-label ${!isAvailable ? 'disabled' : ''}"
                           data-bs-toggle="tooltip"
                           title="${safeValue}${!isAvailable ? ' (Sold Out)' : ''}">
                      <img class="variant-card-img"
                           src="${this.escapeHtml(imgUrl)}"
                           alt="${safeValue}"
                           width="40"
                           height="40"
                           loading="eager"
                           onerror="this.src='data:image/svg+xml,<svg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 40 40%22><rect width=%2240%22 height=%2240%22 fill=%22%23f0f0f0%22 rx=%2220%22/></svg>'">
                    </label>
                  </li>
                `;
              }).join('')}
            </ul>
            <h4 class="variant-card-title title d-flex h6 mb-3 bundle-step-header">
              ${safeOptionName}:
              <span class="text-muted ms-2">${this.escapeHtml(selectedValue)}</span>
            </h4>
          </div>
        `;
      } else {
        // BUG-032 FIX: Text options also use card style (Ring Size, etc.)
        // BUG-099: Added step numbers
        return `
          <div class="product-option-wrapper bundle-variant-selector mb-4" data-handle="${optionHandle}">
            <ul class="variant-card-list list-unstyled mb-0"
                data-option-name="${safeOptionName}" data-option-index="${optIndex}">
              ${uniqueValues.map(value => {
                const isSelected = value === selectedValue;
                const isAvailable = productData.variants.some(v =>
                  v[optionKey] === value && v.available
                );
                const valueHandle = value.toLowerCase().replace(/[^a-z0-9]+/g, '-');
                const safeValue = this.escapeHtml(value);

                return `
                  <li class="variant-card-item">
                    <input type="radio"
                           name="bundle-individual-option-${optIndex}"
                           id="bundle-individual-${optIndex}-${valueHandle}"
                           class="bundle-individual-variant-input visually-hidden"
                           data-option-name="${safeOptionName}"
                           data-option-value="${safeValue}"
                           ${isSelected ? 'checked' : ''}
                           ${!isAvailable ? 'disabled' : ''}>
                    <label for="bundle-individual-${optIndex}-${valueHandle}"
                           class="variant-card-label ${!isAvailable ? 'disabled' : ''}">
                      ${safeValue}${!isAvailable ? ' (Sold Out)' : ''}
                    </label>
                  </li>
                `;
              }).join('')}
            </ul>
            <h4 class="variant-card-title title d-flex h6 mb-3 bundle-step-header">
              ${safeOptionName}:
              <span class="text-muted ms-2">${this.escapeHtml(selectedValue)}</span>
            </h4>
          </div>
        `;
      }
    }).join('');

    // Close variant content div
    html += `</div>`;

    // Close variant section div
    html += `</div>`;

    return html;
  }

  /**
   * Render pet name inputs based on pendant count
   * BUG-099: Added step numbers
   * @param {Object} product - Current product object
   * @param {Object} stepNumbers - Step number mapping
   */
  renderPetNameInputs(product, stepNumbers = {}) {
    // Check if product requires pet names
    if (!product.requirements?.requiresPetName) {
      return '';
    }

    const pendantCount = product.pendantCount || 1;
    const charLimit = product.requirements?.charLimit || 8;
    const noCaps = product.requirements?.noCaps || false;
    const petNames = product.customization?.petNames || [];

    const inputs = Array(pendantCount).fill(0).map((_, i) => {
      const value = petNames[i] || '';
      const label = pendantCount > 1 ? `Pendant ${i + 1} Name` : "Your Pet's Name";

      return `
        <div class="bundle-petname-input-group mb-3">
          <label class="form-label">${label}</label>
          <div class="input-group">
            <input type="text"
                   class="form-control bundle-individual-petname-input"
                   data-petname-index="${i}"
                   data-no-caps="${noCaps}"
                   maxlength="${charLimit}"
                   placeholder="Enter name"
                   value="${this.escapeHtml(value)}">
            <span class="input-group-text bundle-petname-counter">
              ${value.length}/${charLimit}
            </span>
          </div>
        </div>
      `;
    }).join('');

    // Show info if multiple pendants
    const multiInfo = pendantCount > 1 ? `
      <div class="alert alert-info py-2 mb-3">
        <small>You've selected ${pendantCount} pendants. Please enter a name for each.</small>
      </div>
    ` : '';

    // BUG-099: Step number
    const stepNum = stepNumbers.petName || '';
    const stepLabel = stepNum ? `<span class="bundle-step-number">Step ${stepNum}:</span>` : '';

    return `
      <div class="bundle-individual-petnames mb-4">
        <h5 class="bundle-step-header mb-3">${stepLabel}Enter your pet's name</h5>
        ${multiInfo}
        ${inputs}
      </div>
    `;
  }

  /**
   * Render photo upload section
   * BUG-099: Added step numbers and Photo Guide button
   * @param {Object} product - Current product object
   * @param {Object} stepNumbers - Step number mapping
   */
  renderPhotoSection(product, stepNumbers = {}) {
    if (!product.requirements?.requiresPhoto) {
      return '';
    }

    const index = this.currentProductIndex;
    const stepNum = stepNumbers.photo || '';
    const stepLabel = stepNum ? `<span class="bundle-step-number">Step ${stepNum}:</span>` : '';

    return `
      <div class="bundle-individual-photo mb-4">
        <h5 class="bundle-step-header mb-3">
          ${stepLabel}Add your pet's picture <span class="text-danger">*</span>
        </h5>

        <!-- BUG-099: Photo Guide Button -->
        <button type="button" class="bundle-photo-guide-btn mb-3"
                data-bs-toggle="modal" data-bs-target="#bundle-photo-guide-modal">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM19 19H5V5H19V19Z" fill="currentColor"/>
            <path d="M14.04 12.79L11.79 15.79L10.29 14.29C10.1 14.1 9.8 14.1 9.61 14.29L6.71 17.19C6.52 17.38 6.52 17.69 6.71 17.88C6.9 18.07 7.21 18.07 7.4 17.88L9.9 15.38L11.4 16.88C11.59 17.07 11.9 17.07 12.09 16.88L14.75 13.47L17.25 15.97C17.44 16.16 17.75 16.16 17.94 15.97C18.13 15.78 18.13 15.47 17.94 15.28L14.79 12.13C14.6 11.94 14.29 11.94 14.1 12.13L14.04 12.79Z" fill="currentColor"/>
            <path d="M8.5 11C9.32843 11 10 10.3284 10 9.5C10 8.67157 9.32843 8 8.5 8C7.67157 8 7 8.67157 7 9.5C7 10.3284 7.67157 11 8.5 11Z" fill="currentColor"/>
          </svg>
          <span>Photo Guide</span>
        </button>

        <div id="bundle-photo-container-individual-${index}"></div>
      </div>
    `;
  }

  /**
   * Initialize photo uploader widget
   */
  async initPhotoUploader() {
    const product = this.getCurrentProduct();
    if (!product?.requirements?.requiresPhoto) return;

    const index = this.currentProductIndex;
    const container = document.getElementById(`bundle-photo-container-individual-${index}`);
    if (!container) return;

    // Create photo uploader instance if needed, cleanup previous if exists
    if (this.photoUploader) {
      this.photoUploader.cleanup();
    } else {
      this.photoUploader = new BundlePhotoUploader(this.stateManager);
    }

    // Get existing photo URL from product state
    const existingUrl = product.customization?.photoUrl || null;

    // BUG-046 FIX: Capture context at callback registration time
    const capturedProductIndex = this.currentProductIndex;
    const capturedToken = this.asyncToken;
    const capturedProductId = product.productId;

    // Initialize uploader
    await this.photoUploader.initForIndividual(container, index, existingUrl, (photoUrl) => {
      // BUG-046 FIX: Validate context before executing
      if (this.asyncToken !== capturedToken || this.currentProductIndex !== capturedProductIndex) {
        console.warn('[BundleIndividual] Photo callback ignored - product changed during upload');
        return;
      }
      this.handlePhotoChange(photoUrl, capturedProductIndex, capturedProductId);
    });
  }

  /**
   * Handle photo upload/change
   * @param {string|null} photoUrl - The uploaded photo URL or null if removed
   * @param {number|null} productIndex - The product index (BUG-046 FIX: captured at callback registration)
   * @param {number|null} productId - The product ID (BUG-046 FIX: captured at callback registration)
   */
  handlePhotoChange(photoUrl, productIndex = null, productId = null) {
    // BUG-045 FIX: Use captured productIndex for update (handles duplicate products)
    // When same product is added multiple times, productId is not unique.
    // We must use index-based updates to ensure correct product is updated.
    let targetIndex;
    let product;

    if (productIndex !== null) {
      targetIndex = productIndex;
      const state = this.stateManager.get();
      product = state.products[productIndex];
    } else {
      targetIndex = this.getProductIndex();
      product = this.getCurrentProduct();
    }

    if (!product || targetIndex < 0) return;

    // BUG-045 FIX: Update by INDEX, not productId (handles duplicate products)
    this.stateManager.updateProductByIndex(targetIndex, {
      customization: {
        ...product.customization,
        photoUrl
      }
    });

    // Phase 2: Notify UI of customization change for mobile bar validation
    document.dispatchEvent(new CustomEvent('bundle:customization-updated'));
  }

  // ═══════════════════════════════════════════════════════════════
  // IMAGE GALLERY - BUG-087 FIX
  // ═══════════════════════════════════════════════════════════════

  /**
   * Initialize Splide carousel for gallery
   * BUG-087 FIX: Full image gallery like Quick View modal
   */
  initGalleryCarousel() {
    if (typeof Splide === 'undefined') return;

    this.destroyGalleryCarousel();

    const uniqueId = `bundle-individual-gallery-${this.currentProductIndex}`;
    const thumbsId = `${uniqueId}-thumbs`;
    const mainEl = document.getElementById(uniqueId);
    const thumbsEl = document.getElementById(thumbsId);

    if (!mainEl) return;

    if (thumbsEl) {
      // BUG-099 FIX: Arrows on thumbnail row, not main gallery (users can swipe main)
      this.individualThumbsSplide = new Splide(thumbsEl, {
        rewind: true,
        fixedWidth: 60,
        fixedHeight: 60,
        gap: 6,
        isNavigation: true,
        pagination: false,
        arrows: false  // BUG-112: Disable arrows (causing visual dots)
      });

      this.individualSplide = new Splide(mainEl, {
        type: 'fade',
        rewind: true,
        pagination: false,
        arrows: false,  // BUG-099: Disable arrows on main (users swipe)
        speed: 250
      });

      this.individualSplide.sync(this.individualThumbsSplide);
      this.individualSplide.mount();
      this.individualThumbsSplide.mount();
    } else {
      // Single image or no thumbnails - show pagination dots instead
      this.individualSplide = new Splide(mainEl, {
        type: 'fade',
        rewind: true,
        pagination: true,
        arrows: false,  // BUG-099: No arrows, users swipe
        speed: 250
      });
      this.individualSplide.mount();
    }
  }

  /**
   * Destroy Splide instances - prevents memory leaks
   * BUG-087 FIX
   */
  destroyGalleryCarousel() {
    if (this.individualSplide) {
      try { this.individualSplide.destroy(); } catch (e) {}
      this.individualSplide = null;
    }
    if (this.individualThumbsSplide) {
      try { this.individualThumbsSplide.destroy(); } catch (e) {}
      this.individualThumbsSplide = null;
    }
  }

  /**
   * Sync gallery to variant image
   * BUG-087 FIX: When user changes variant, scroll to matching image
   * @param {string} variantImageSrc - Variant image URL
   */
  syncGalleryToVariant(variantImageSrc) {
    if (!this.individualSplide || !variantImageSrc) return;

    const productData = this.getProductData();
    if (!productData?.images) return;

    // Extract filename (strip query string and size suffix)
    const variantFilename = variantImageSrc
      .split('?')[0].split('/').pop().replace(/_\d+x\d+/, '');

    const imageIndex = productData.images.findIndex(img => {
      const imgFilename = img.split('?')[0].split('/').pop().replace(/_\d+x\d+/, '');
      return imgFilename.includes(variantFilename) || variantFilename.includes(imgFilename);
    });

    if (imageIndex >= 0) {
      this.individualSplide.go(imageIndex);
    }
  }

  // ═══════════════════════════════════════════════════════════════
  // ENGRAVING
  // ═══════════════════════════════════════════════════════════════

  /**
   * Render inline engraving inputs for current product
   * @param {Object} product - Current product
   * @returns {string} HTML string
   */
  renderEngravingSection(product, stepNumbers = {}) {
    // BUG-038 FIX: Only show engraving for products with "engraving-on-back" tag
    // This prevents the engraving popup from showing for products that don't support it
    const productData = this.getProductData();
    const productTags = productData?.tags || [];
    // Shopify tags can be a comma-separated string or an array
    const tagsArray = Array.isArray(productTags)
      ? productTags
      : (typeof productTags === 'string' ? productTags.split(',').map(t => t.trim()) : []);

    const hasEngravingTag = tagsArray.some(tag =>
      tag.toLowerCase() === 'engraving-on-back'
    );

    // If product doesn't have the engraving tag, return empty (no engraving section)
    if (!hasEngravingTag) {
      return '';
    }

    const pendantCount = product.pendantCount || 1;
    const pendantSize = product.pendantSize || '1.5 cm';
    const maxLines = pendantSize.includes('2.0') ? 3 : 2;
    const maxChars = 9;
    const engraving = product.customization?.engraving || {};
    const petNames = product.customization?.petNames || [];

    // Build HTML for each pendant
    const pendantInputs = Array(pendantCount).fill(0).map((_, i) => {
      const pendantNum = i + 1;
      const pendantEngraving = engraving[pendantNum] || { lines: [], hasText: false };
      const petName = petNames[i] || `Pendant ${pendantNum}`;

      // Build line inputs
      const lineInputs = Array(maxLines).fill(0).map((_, lineIdx) => {
        const value = pendantEngraving.lines[lineIdx] || '';
        return `
          <div class="bundle-engraving-line mb-2">
            <div class="input-group input-group-sm">
              <span class="input-group-text">Line ${lineIdx + 1}</span>
              <input type="text"
                     class="form-control bundle-engraving-input"
                     data-pendant-num="${pendantNum}"
                     data-line-index="${lineIdx}"
                     maxlength="${maxChars}"
                     placeholder="Optional"
                     value="${this.escapeHtml(value)}">
              <span class="input-group-text bundle-engraving-counter">
                ${value.length}/${maxChars}
              </span>
            </div>
          </div>
        `;
      }).join('');

      return `
        <div class="bundle-engraving-pendant mb-3">
          <label class="form-label text-muted">
            ${pendantCount > 1 ? `${this.escapeHtml(petName)}'s Back Engraving` : 'Back Engraving'}
            <span class="badge bg-secondary ms-2">+$10</span>
          </label>
          ${lineInputs}
        </div>
      `;
    }).join('');

    // BUG-099: Add step number
    const stepNum = stepNumbers.engraving || '';
    const stepLabel = stepNum ? `<span class="bundle-step-number">Step ${stepNum}:</span> ` : '';

    return `
      <div class="bundle-individual-engraving mb-4">
        <h5 class="mb-3">
          ${stepLabel}Add Back Engraving <small class="text-muted">(Optional)</small>
        </h5>
        <p class="text-muted small mb-3">
          Personalize the back of your pendant${pendantCount > 1 ? 's' : ''} with custom text.
        </p>
        ${pendantInputs}
      </div>
    `;
  }

  /**
   * Handle engraving input change
   * @param {Event} e - Input event
   */
  handleEngravingInput(e) {
    const pendantNum = parseInt(e.target.dataset.pendantNum, 10);
    const lineIndex = parseInt(e.target.dataset.lineIndex, 10);
    let value = this.removeEmojis(e.target.value);

    // Enforce uppercase
    value = value.toUpperCase();

    // Update input if emojis removed or case changed
    if (e.target.value !== value) {
      e.target.value = value;
    }

    // Update counter
    const counter = e.target.closest('.input-group')?.querySelector('.bundle-engraving-counter');
    if (counter) {
      counter.textContent = `${value.length}/9`;
    }

    // Save to state
    const product = this.getCurrentProduct();
    if (!product) return;

    const engraving = { ...(product.customization?.engraving || {}) };
    const pendantEngraving = { ...(engraving[pendantNum] || { lines: [], hasText: false }) };

    // Update lines array
    pendantEngraving.lines = [...(pendantEngraving.lines || [])];
    pendantEngraving.lines[lineIndex] = value;
    pendantEngraving.hasText = pendantEngraving.lines.some(line => line && line.trim() !== '');

    engraving[pendantNum] = pendantEngraving;

    // BUG-045 FIX: Update by INDEX, not productId (handles duplicate products)
    const targetIndex = this.getProductIndex();
    this.stateManager.updateProductByIndex(targetIndex, {
      customization: {
        ...product.customization,
        engraving
      }
    });
  }

  /**
   * Remove emojis from text
   * @param {string} text - Input text
   * @returns {string} Text with emojis removed
   */
  removeEmojis(text) {
    if (!text) return '';
    const emojiRegex = /[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F900}-\u{1F9FF}]|[\u{1F000}-\u{1F02F}]|[\u{1F0A0}-\u{1F0FF}]|[\u{1F100}-\u{1F1FF}]|[\u{1F200}-\u{1F2FF}]|[\u{1F700}-\u{1F77F}]|[\u{1F780}-\u{1F7FF}]|[\u{1F800}-\u{1F8FF}]|[\u{1FA00}-\u{1FA6F}]|[\u{1FA70}-\u{1FAFF}]|[\u{1FB00}-\u{1FBFF}]/gu;
    return text.replace(emojiRegex, '');
  }

  /**
   * Update engraving section after variant change (pendant count may change)
   */
  updateEngravingSection() {
    const product = this.getCurrentProduct();
    if (!product) return;

    const engravingSection = this.container.querySelector('.bundle-individual-engraving');
    if (engravingSection) {
      engravingSection.outerHTML = this.renderEngravingSection(product);
    }
  }

  /**
   * Render product description
   * BUG-088 FIX: Show product description during customization
   * @param {Object} productData - Product JSON from Shopify
   * @param {number} index - Product index for unique accordion IDs
   * @returns {string} HTML string
   * Design refactor: Converted to Bootstrap 5 accordion, collapsed by default
   */
  renderDescription(productData, index = 0) {
    if (!productData) return '';

    // Get description from available properties
    const description = productData.body_html || productData.description || '';

    // Validate content exists and is meaningful
    const trimmed = description.trim();
    if (!trimmed || trimmed === '<p></p>' || trimmed === '<br>') {
      return '';
    }

    const accordionId = `bundle-desc-accordion-${index}`;
    const collapseId = `bundle-desc-collapse-${index}`;

    // BUG-113: Accordion EXPANDED by default (user requested "open the accordion")
    return `
      <div class="accordion bundle-description-accordion mb-4" id="${accordionId}">
        <div class="accordion-item border-0">
          <h2 class="accordion-header">
            <button class="accordion-button"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#${collapseId}"
                    aria-expanded="true"
                    aria-controls="${collapseId}">
              Details
            </button>
          </h2>
          <div id="${collapseId}" class="accordion-collapse collapse show" data-bs-parent="#${accordionId}">
            <div class="accordion-body">
              <div class="rte">${trimmed}</div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Render navigation buttons
   */
  renderNavigation(index) {
    const isFirst = index === 0;
    const isLast = index === 2;

    const backLabel = isFirst ? '← Back to Options' : `← Back to Product ${index}`;
    const continueLabel = isLast ? 'Continue to Extras →' : `Next: Product ${index + 2} →`;

    return `
      <div class="bundle-individual-nav d-flex gap-3 mt-4">
        <button class="btn btn-outline-secondary flex-fill" data-bundle-individual-back>
          ${backLabel}
        </button>
        <button class="btn btn-primary flex-fill" data-bundle-individual-continue>
          ${continueLabel}
        </button>
      </div>
    `;
  }

  // ═══════════════════════════════════════════════════════════════
  // EVENT HANDLING
  // ═══════════════════════════════════════════════════════════════

  /**
   * Bind event listeners
   */
  bindEvents() {
    // Clean up existing handlers first
    this.cleanup();

    // BUG-032 FIX: Variant selection with new card-style selectors
    this.boundVariantHandler = (e) => {
      const cardLabel = e.target.closest('.variant-card-label');
      if (cardLabel && !cardLabel.classList.contains('disabled')) {
        const input = cardLabel.previousElementSibling;
        if (input && input.classList.contains('bundle-individual-variant-input') && !input.disabled) {
          input.checked = true;
          const optionName = input.dataset.optionName;
          const optionValue = input.dataset.optionValue;

          // Update the title span with selected value
          const wrapper = cardLabel.closest('.product-option-wrapper');
          if (wrapper) {
            const titleSpan = wrapper.querySelector('.variant-card-title span.text-muted');
            if (titleSpan) {
              titleSpan.textContent = optionValue;
            }
          }

          this.handleVariantChange(optionName, optionValue);
        }
      }
    };
    this.container.addEventListener('click', this.boundVariantHandler);

    // Pet name input handler
    this.boundPetNameHandler = (e) => {
      if (e.target.classList.contains('bundle-individual-petname-input')) {
        const index = parseInt(e.target.dataset.petnameIndex, 10);
        this.handlePetNameInput(e.target.value, index, e.target);
      }
    };
    this.container.addEventListener('input', this.boundPetNameHandler);

    // Engraving input handler
    this.boundEngravingHandler = (e) => {
      if (e.target.classList.contains('bundle-engraving-input')) {
        this.handleEngravingInput(e);
      }
    };
    this.container.addEventListener('input', this.boundEngravingHandler);

    // Continue button handler
    this.boundContinueHandler = (e) => {
      if (e.target.closest('[data-bundle-individual-continue]')) {
        this.handleContinue();
      }
    };
    this.container.addEventListener('click', this.boundContinueHandler);

    // Back button handler
    this.boundBackHandler = (e) => {
      if (e.target.closest('[data-bundle-individual-back]')) {
        this.handleBack();
      }
    };
    this.container.addEventListener('click', this.boundBackHandler);
  }

  // ═══════════════════════════════════════════════════════════════
  // VARIANT HANDLING
  // ═══════════════════════════════════════════════════════════════

  /**
   * Handle variant button click
   */
  handleVariantChange(optionName, optionValue) {
    const product = this.getCurrentProduct();
    const productData = this.getProductData();
    if (!product || !productData) return;

    // BUG-032 FIX: Card-based selectors use radio inputs
    // Visual selection is handled by CSS :checked state on the radio input
    // No manual button class toggling needed

    // Start from the selected variant's options: quick-add and quick view store no
    // selectedOptions, and {} plus the clicked value matches any variant with that value
    const currentVariant = productData.variants.find(v => v.id === product.selectedVariantId);
    const currentOptions = currentVariant
      ? this.getVariantOptions(productData, currentVariant)
      : { ...product.selectedOptions };

    const matchingVariant = this.pickVariantForOption(productData, currentOptions, optionName, optionValue);

    if (matchingVariant) {
      const oldPendantCount = product.pendantCount;
      const oldPendantSize = product.pendantSize;
      const newPendantCount = this.extractPendantCount(matchingVariant, productData);
      const newPendantSize = this.extractPendantSize(matchingVariant, productData);
      const selectedOptions = this.getVariantOptions(productData, matchingVariant);

      const updates = {
        selectedVariantId: matchingVariant.id,
        selectedVariantTitle: matchingVariant.title,
        selectedVariantImage: matchingVariant.featured_image?.src || product.featuredImage,
        selectedOptions,
        pendantCount: newPendantCount,
        pendantSize: newPendantSize
      };
      // Names and engravings beyond the pendant count stay in state so switching back restores
      // them; inputs render only pendantCount pendants and the cart build caps at pendantCount.

      // BUG-045 FIX: Update by INDEX, not productId (handles duplicate products)
      const targetIndex = this.getProductIndex();
      this.stateManager.updateProductByIndex(targetIndex, updates);

      // The fallback variant can change other options too
      this.syncVariantInputs(selectedOptions);

      // Phase 2: Notify UI of customization change for mobile bar validation
      document.dispatchEvent(new CustomEvent('bundle:customization-updated'));

      // Update thumbnail with crossfade
      this.updateThumbnail(matchingVariant.featured_image?.src || product.featuredImage);

      // BUG-087 FIX: Sync gallery carousel to variant image
      if (matchingVariant.featured_image?.src) {
        this.syncGalleryToVariant(matchingVariant.featured_image.src);
      }

      // Sync progress bar thumbnails
      const state = this.stateManager.get();
      this.uiManager?.renderThumbnails?.(state.products);

      // If pendant count or size changed, re-render pet name inputs and engraving section
      if (newPendantCount !== oldPendantCount || newPendantSize !== oldPendantSize) {
        this.updatePetNameSection();
        this.updateEngravingSection();
      }
    } else {
      // BUG-053 FIX: Never select a sold-out variant (also when the cache is stale)
      this.uiManager?.showToast?.('This variant is sold out.', 'error');
      this.syncVariantInputs(currentOptions);
    }
  }

  /**
   * Choose the variant for a clicked option value: an available variant with that value that
   * keeps every other "pendant" option (so the pendant count, and the names it needs, never
   * change implicitly) and as many other current options as possible - the exact combination
   * first. Returns null when no such variant exists.
   * @param {Object} productData - Product data
   * @param {Object} currentOptions - Option name -> value map of the current selection
   * @param {string} optionName - Clicked option name
   * @param {string} optionValue - Clicked option value
   * @returns {Object|null} Variant or null
   */
  pickVariantForOption(productData, currentOptions, optionName, optionValue) {
    const optionNames = productData.options.map(o => (typeof o === 'string' ? o : o.name));
    const clickedIndex = optionNames.findIndex(name => name.toLowerCase() === optionName.toLowerCase());
    if (clickedIndex === -1) return null;

    let best = null;
    let bestKept = -1;
    productData.variants.forEach(variant => {
      if (!variant.available || variant[`option${clickedIndex + 1}`] !== optionValue) return;

      let kept = 0;
      for (let i = 0; i < optionNames.length; i++) {
        if (i === clickedIndex || !(optionNames[i] in currentOptions)) continue;
        const keeps = variant[`option${i + 1}`] === currentOptions[optionNames[i]];
        if (!keeps && optionNames[i].toLowerCase().includes('pendant')) return;
        if (keeps) kept++;
      }

      if (kept > bestKept) {
        best = variant;
        bestKept = kept;
      }
    });
    return best;
  }

  /**
   * Option name -> value map for a variant
   * @param {Object} productData - Product data
   * @param {Object} variant - Variant object
   * @returns {Object} Selected options
   */
  getVariantOptions(productData, variant) {
    const options = {};
    if (!variant) return options;
    productData.options.forEach((opt, i) => {
      const name = typeof opt === 'string' ? opt : opt.name;
      options[name] = variant[`option${i + 1}`];
    });
    return options;
  }

  /**
   * Check the option inputs that match the given options and update their titles
   * @param {Object} selectedOptions - Option name -> value map
   */
  syncVariantInputs(selectedOptions) {
    this.container?.querySelectorAll('.bundle-individual-variant-input').forEach(input => {
      const value = selectedOptions[input.dataset.optionName];
      if (value === undefined) return;
      input.checked = input.dataset.optionValue === value;
      if (input.checked) {
        const titleSpan = input.closest('.product-option-wrapper')?.querySelector('.variant-card-title span.text-muted');
        if (titleSpan) titleSpan.textContent = value;
      }
    });
  }

  /**
   * Extract pendant count from variant
   */
  extractPendantCount(variant, productData) {
    for (let i = 0; i < productData.options.length; i++) {
      // BUG-018 FIX: Handle both string and object formats for product.options
      const opt = productData.options[i];
      const optionName = (typeof opt === 'string' ? opt : opt.name).toLowerCase();
      if (optionName.includes('pendant')) {
        const value = parseInt(variant[`option${i + 1}`], 10);
        if (value >= 1 && value <= 4) return value;
      }
    }
    return 1;
  }

  /**
   * Extract pendant size from variant (1.5 cm or 2.0 cm)
   * @param {Object} variant - Variant object
   * @param {Object} productData - Product data
   * @returns {string} Pendant size ('1.5 cm' or '2.0 cm')
   */
  extractPendantSize(variant, productData) {
    // Check variant title for size
    const title = variant.title?.toLowerCase() || '';
    if (title.includes('2.0') || title.includes('2.0cm') || title.includes('large')) {
      return '2.0 cm';
    }

    // Check each option value for size
    for (let i = 1; i <= 3; i++) {
      const optVal = variant[`option${i}`]?.toLowerCase() || '';
      if (optVal.includes('2.0') || optVal.includes('large')) {
        return '2.0 cm';
      }
    }

    // Check product tags
    const tags = productData.tags?.map(t => t.toLowerCase()) || [];
    if (tags.some(t => t.includes('2.0') || t.includes('large-pendant'))) {
      return '2.0 cm';
    }

    return '1.5 cm';
  }

  /**
   * Update thumbnail image with crossfade
   */
  updateThumbnail(imageSrc) {
    const thumb = this.container?.querySelector('#bundle-individual-preview-image');
    if (thumb) {
      thumb.style.opacity = '0.5';
      thumb.src = this.resizeImage(imageSrc, '150x150');

      // BUG-046 FIX: Track timer for cleanup
      const timerId = setTimeout(() => {
        // BUG-046 FIX: Verify element still valid before modifying
        if (thumb.isConnected) {
          thumb.style.opacity = '1';
        }
      }, 150);
      this.pendingTimers.push(timerId);
    }
  }

  /**
   * Update pet name section (on pendant count change)
   */
  updatePetNameSection() {
    const product = this.getCurrentProduct();
    if (!product) return;

    // Find and replace the pet names section
    const petnamesSection = this.container.querySelector('.bundle-individual-petnames');
    if (petnamesSection) {
      petnamesSection.outerHTML = this.renderPetNameInputs(product);
    }
  }

  // ═══════════════════════════════════════════════════════════════
  // PET NAME HANDLING
  // ═══════════════════════════════════════════════════════════════

  /**
   * Handle pet name input
   */
  handlePetNameInput(value, index, inputEl) {
    const product = this.getCurrentProduct();
    if (!product) return;

    const noCaps = inputEl.dataset.noCaps === 'true';
    const charLimit = parseInt(inputEl.maxLength, 10) || 8;

    // Transform based on NoCaps setting
    let transformedValue = value;
    if (noCaps) {
      // Title case: first letter uppercase, rest lowercase
      transformedValue = value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
    } else {
      // All uppercase
      transformedValue = value.toUpperCase();
    }

    // Update input if transformed
    if (inputEl.value !== transformedValue) {
      inputEl.value = transformedValue;
    }

    // Update counter
    const counter = inputEl.closest('.input-group')?.querySelector('.bundle-petname-counter');
    if (counter) {
      counter.textContent = `${transformedValue.length}/${charLimit}`;
    }

    // Update state for THIS product
    const petNames = [...(product.customization?.petNames || [])];
    petNames[index] = transformedValue;

    // BUG-045 FIX: Update by INDEX, not productId (handles duplicate products)
    const targetIndex = this.getProductIndex();
    this.stateManager.updateProductByIndex(targetIndex, {
      customization: {
        ...product.customization,
        petNames
      }
    });

    // Phase 2: Notify UI of customization change for mobile bar validation
    document.dispatchEvent(new CustomEvent('bundle:customization-updated'));
  }

  // ═══════════════════════════════════════════════════════════════
  // NAVIGATION
  // ═══════════════════════════════════════════════════════════════

  /**
   * Handle continue button
   * Phase 9: Added shake animation and input error highlighting on validation failure
   */
  handleContinue() {
    const validation = this.validateCurrentProduct();

    if (!validation.valid) {
      this.uiManager?.showToast?.(validation.message, 'warning');

      // Phase 9: Add shake animation to form container
      const formContainer = this.container.querySelector('.bundle-individual-content');
      if (formContainer && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        formContainer.classList.add('bundle-shake');
        setTimeout(() => formContainer.classList.remove('bundle-shake'), 500);
      }

      // Phase 9: Highlight empty pet name inputs
      const product = this.getCurrentProduct();
      if (product?.requirements?.requiresPetName) {
        const petNameInputs = this.container.querySelectorAll('.bundle-individual-petname-input');
        const petNames = product.customization?.petNames || [];
        petNameInputs.forEach((input, index) => {
          if (!petNames[index] || petNames[index].trim() === '') {
            input.classList.add('bundle-input-error');
            setTimeout(() => input.classList.remove('bundle-input-error'), 1200);
            // Focus first invalid input
            if (index === 0 || !petNames[index - 1]) {
              input.focus();
            }
          }
        });
      }

      return;
    }

    // Navigate based on current index
    const index = this.currentProductIndex;

    if (index === 2) {
      // Last product - go to upsells
      this.stateManager.goToStep('upsells');
    } else {
      // Next product
      const nextStep = `individual-${index + 2}`;
      this.stateManager.goToStep(nextStep);
    }
  }

  /**
   * Handle back button
   */
  handleBack() {
    const index = this.currentProductIndex;

    if (index === 0) {
      // BUG-067 FIX: Go back to selection (not choice) and clear customization state
      // The choice modal should only appear when clicking "Next Step" from selection
      this.stateManager.set({
        customizationMode: null,
        hasCommonVariants: false,
        commonVariants: null,
        unifiedCustomization: null
      });
      this.stateManager.goToStep('selection');
    } else {
      // Previous product
      const prevStep = `individual-${index}`;
      this.stateManager.goToStep(prevStep);
    }
  }

  // ═══════════════════════════════════════════════════════════════
  // VALIDATION
  // ═══════════════════════════════════════════════════════════════

  /**
   * Validate current product is complete
   */
  validateCurrentProduct() {
    const product = this.getCurrentProduct();

    if (!product) {
      return { valid: false, message: 'Product not found' };
    }

    // Must have variant selected
    if (!product.selectedVariantId) {
      return { valid: false, message: 'Please select product options' };
    }

    // Check pet name requirement
    if (product.requirements?.requiresPetName) {
      const pendantCount = product.pendantCount || 1;
      const petNames = product.customization?.petNames || [];

      for (let i = 0; i < pendantCount; i++) {
        if (!petNames[i] || petNames[i].trim() === '') {
          return {
            valid: false,
            message: pendantCount > 1
              ? `Please enter a name for pendant ${i + 1}`
              : "Please enter your pet's name"
          };
        }
      }
    }

    // Photo validation - Strict mode: block if required photo missing
    if (product.requirements?.requiresPhoto && !product.customization?.photoUrl) {
      // Scroll to photo section for user attention
      const photoSection = document.querySelector('.bundle-individual-photo');
      if (photoSection) {
        photoSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return { valid: false, message: 'Please upload a photo for this product' };
    }

    return { valid: true };
  }

  // ═══════════════════════════════════════════════════════════════
  // CLEANUP
  // ═══════════════════════════════════════════════════════════════

  /**
   * Cleanup event listeners
   */
  cleanup() {
    // BUG-046 FIX: Cancel all pending timers
    if (this.pendingTimers && this.pendingTimers.length > 0) {
      this.pendingTimers.forEach(id => clearTimeout(id));
      this.pendingTimers = [];
    }

    // BUG-087 FIX: Destroy Splide gallery instances
    this.destroyGalleryCarousel();

    // BUG-113: Counter is now in validation element, no separate cleanup needed

    if (!this.container) return;

    if (this.boundVariantHandler) {
      this.container.removeEventListener('click', this.boundVariantHandler);
    }
    if (this.boundPetNameHandler) {
      this.container.removeEventListener('input', this.boundPetNameHandler);
    }
    if (this.boundEngravingHandler) {
      this.container.removeEventListener('input', this.boundEngravingHandler);
    }
    if (this.boundContinueHandler) {
      this.container.removeEventListener('click', this.boundContinueHandler);
    }
    if (this.boundBackHandler) {
      this.container.removeEventListener('click', this.boundBackHandler);
    }

    this.boundVariantHandler = null;
    this.boundPetNameHandler = null;
    this.boundEngravingHandler = null;
    this.boundContinueHandler = null;
    this.boundBackHandler = null;

    // Cleanup photo uploader
    if (this.photoUploader) {
      this.photoUploader.cleanup();
      this.photoUploader = null;
    }
  }
}

// Export for other modules
window.BundleIndividualManager = BundleIndividualManager;
